import { useCallback } from 'react';
import { REDIRECT_ERROR_URL_WRONG_ID } from '@/creditflowresearch/application/League/constants';
import { ITablePositionPayload, TUpdateFiltersPayload } from '@/creditflowresearch/application/League/Scorecard/types';
import { ISelectedPeriod, IScorecardState, IColumnState, IScorecardCompanyData } from '@/creditflowresearch/application/League/interfaces';
import { createInitialColumnState, createResetPeriod, createInitialCompanyData } from '@/creditflowresearch/application/League/Scorecard/utils/scorecardUtils';
import { IStandingsPayload, IStandingsResponse } from '@/creditflowresearch/domain/vo/League/IStandings';

interface IUseScorecardPageProps {
  companyData: IScorecardCompanyData,
  updateFilters: (filters: TUpdateFiltersPayload) => TUpdateFiltersPayload,
  getStandings: (payload: IStandingsPayload) => Promise<IStandingsResponse>,
  stateActions: {
    setData: (data: Partial<IScorecardState['data']>) => void,
    setIsFetching: (value: boolean) => void,
    setInitialized: (value: boolean) => void,
    setExpandedCategories: (categories: string[]) => void,
    setColumnState: (state: IColumnState | ((prev: IColumnState) => IColumnState)) => void,
    setSelectedPeriod: (period: ISelectedPeriod) => void,
    setCompanyData: (data: IScorecardState['company']['data']) => void,
    setCompanyError: (error: string | null) => void,
    setIsCompanyFetching: (value: boolean) => void,
  },
}

export const useScorecardPage = (dependencies: IUseScorecardPageProps) => {
  const { companyData, getStandings, stateActions } = dependencies;

  const getPageData = useCallback(async ({ productIds, regionIds, companyRole }: ITablePositionPayload) => {
    const standingsFiltersPayload = {
      filters: {
        includedProductIds: productIds || [],
        includedRegionIds: regionIds || [],
        includedCompanyId: companyData.id,
        includedCompanyRole: companyRole,
      },
    };

    await getStandings(standingsFiltersPayload);
  }, [companyData.id, getStandings]);

  const resetPage = useCallback(() => {
    stateActions.setSelectedPeriod(createResetPeriod());
    stateActions.setData({});
    stateActions.setIsFetching(true);
    stateActions.setInitialized(false);
    stateActions.setExpandedCategories([]);
    stateActions.setColumnState(createInitialColumnState());
    stateActions.setCompanyData(createInitialCompanyData());
    stateActions.setCompanyError(null);
    stateActions.setIsCompanyFetching(false);
  }, [stateActions]);

  const setScorecardData = useCallback((payload: Partial<IScorecardState['data']>) => {
    stateActions.setData(payload);
    stateActions.setIsFetching(false);
    stateActions.setInitialized(true);
  }, [stateActions]);

  const setCompanyDataAction = useCallback((payload: IScorecardState['company']['data']) => {
    stateActions.setCompanyData(payload);
    stateActions.setCompanyError(payload.id ? null : REDIRECT_ERROR_URL_WRONG_ID);
    stateActions.setIsCompanyFetching(false);
  }, [stateActions]);

  return {
    getPageData,
    resetPage,
    setScorecardData,
    setCompanyDataAction,
  };
};
